import { locationSearchContext } from '@/features/searchV2/helpers/tracking';
import { SearchContext as TSearchContext } from '@/features/searchV2/types/tracking';
import { PerformSearchOptions } from '@/hooks/search/useSearch.types';
import { AppDispatch } from '@/store';
import { UrlSearchState } from '@/types/state/search';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppDispatch } from '..';

export type FetchArgs = {
  params: UrlSearchState;
  bounds?: string;
  keywordSubmitted?: boolean;
  searchContext?: TSearchContext;
  isLoadMore: boolean;
};

export type SearchManagerCoreConfig = {
  /** Current search params derived from the URL (provided by the consumer). */
  params: UrlSearchState;

  /** Fetches results and dispatches to Redux. Called by the URL-driven effect. */
  fetchResults: (args: FetchArgs, dispatch: AppDispatch) => Promise<void>;

  /** callback running when results are fetched on SSR on first render */
  didFetchResultsSSR: () => void;

  /**
   * Builds the URL to push when performSearch is called.
   * Returns { pathname, search } for history.push.
   */
  buildUrl: (
    mergedParams: UrlSearchState,
    currentParams: UrlSearchState,
    options?: PerformSearchOptions,
  ) => { pathname: string; search: string } | null;

  /** Redux action type string dispatched on unmount to reset state */
  resetAction: string;

  /** Hook that returns the relevant Redux state slice */
  useSearchState: () => CoreSearchState;

  /** Debounce delay in ms for fetch requests (0 or undefined = no debounce) */
  debounceMs?: number;

  /**
   * Optional callback to check if results for a given page are already cached.
   * When provided, the core skips fetching if cached results exist.
   */
  getPageCache?: (page: number) => any[] | undefined;

  /** Redux action type dispatched after consuming SSR hydration to clear the flag */
  consumeSSRAction?: string;
};

/** Minimal Redux state the core needs. Consumers read their full state via useAppSelector. */
export type CoreSearchState = {
  bounds?: string;
  isSSRFetched: boolean;
  ssrRenderedPage?: number;
  searchContext?: TSearchContext;
  isIndexable: boolean;
};

/**
 * URL-driven search manager core.
 *
 * - `performSearch(newParams)` only writes to the URL via `history.push`
 * - `useEffect([params])` fires on every URL change and calls `config.fetchResults`
 * - Load-more is detected automatically (page incremented by 1, nothing else changed)
 * - Back/forward navigation handled automatically (URL changes → effect fires)
 *
 * The consumer provides `params` (from whatever URL-reading hook it uses)
 * so this core is not coupled to any specific search type.
 */
export function useSearchManagerCore(config: SearchManagerCoreConfig) {
  const {
    params,
    fetchResults,
    didFetchResultsSSR,
    buildUrl,
    resetAction,
    useSearchState,
    debounceMs,
    getPageCache,
    consumeSSRAction,
  } = config;

  const searchState = useSearchState();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation<{ searchContext?: TSearchContext; keywordSubmitted?: boolean }>();
  const keywordSubmitted = location?.state?.keywordSubmitted === true;
  const searchContext = locationSearchContext(history, location?.state);

  const prevParamsRef = useRef<UrlSearchState | null>(null);
  const boundsRef = useRef<string | undefined>(searchState.bounds);
  const skipNextEffectRef = useRef(false);

  // Keep bounds ref in sync
  useEffect(() => {
    boundsRef.current = searchState.bounds;
  }, [searchState.bounds]);

  // Debounced fetch
  const debouncedFetch = useRef(
    debounceMs && debounceMs > 0 ? debounce((fn: () => void) => fn(), debounceMs) : null,
  ).current;

  /**
   * Core URL-driven effect.
   * Fires on every URL change (user search, filter, sort, back/forward navigation).
   */
  useEffect(() => {
    if (keywordSubmitted) {
      history.replace({
        ...location,
        state: { ...location.state, keywordSubmitted: undefined },
      });
    }

    // Allow skipping (e.g. during state cache restoration)
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      prevParamsRef.current = params;
      return;
    }

    // Skip first render if SSR already fetched results
    if (prevParamsRef.current === null && searchState.isSSRFetched) {
      prevParamsRef.current = params;
      didFetchResultsSSR();

      if (consumeSSRAction) {
        dispatch({ type: consumeSSRAction });
      }
      return;
    }

    const prevParams = prevParamsRef.current;
    prevParamsRef.current = params;

    // Detect load-more: only page changed, incremented by 1
    const isLoadMore = Boolean(
      prevParams &&
        params.page === (prevParams.page ?? 1) + 1 &&
        params.q === prevParams.q &&
        params.location === prevParams.location &&
        params.sort === prevParams.sort &&
        params.prefs === prevParams.prefs &&
        params.startDate === prevParams.startDate &&
        params.endDate === prevParams.endDate &&
        params.timeOfDay === prevParams.timeOfDay,
    );

    // Check page cache
    if (getPageCache && !isLoadMore) {
      const cached = getPageCache(params.page ?? 1);
      if (cached && cached.length > 0) {
        return;
      }
    }

    const doFetch = () => {
      fetchResults(
        {
          params,
          bounds: boundsRef.current,
          keywordSubmitted,
          searchContext,
          isLoadMore,
        },
        dispatch,
      );
    };

    if (debouncedFetch) {
      debouncedFetch(doFetch);
    } else {
      doFetch();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch({ type: resetAction });
      debouncedFetch?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * performSearch: merges new params and writes to URL.
   * The effect above will detect the URL change and trigger a fetch.
   */
  const performSearch = useCallback(
    (newParams: UrlSearchState, options?: PerformSearchOptions) => {
      const merged = { ...params, ...newParams };

      // If changing anything except page, reset page to 1
      const isOnlyPageChange = Object.keys(newParams).length === 1 && 'page' in newParams;
      if (!isOnlyPageChange && !('page' in newParams)) {
        merged.page = 1;
      }

      // Handle bounds
      if (options?.bounds !== undefined) {
        boundsRef.current = options.bounds || undefined;
      }

      const url = buildUrl(merged, params, options);

      if (!url) return;

      const isSamePathname = url.pathname === location.pathname;
      const isSameSearchQuery = (url.search ? `?${url.search}` : '') === location.search;

      /**
       * perform Search should still be able to trigger even if
       * the url is unchanged (when user clicks search button manually)
       */
      const isSameUrl = isSamePathname && isSameSearchQuery;
      if (isSameUrl) {
        fetchResults(
          {
            params: merged,
            bounds: boundsRef.current,
            keywordSubmitted: options?.keywordSubmitted,
            searchContext: options?.searchContext,
            isLoadMore: false,
          },
          dispatch,
        );
        return;
      }

      history.push({
        pathname: url.pathname,
        search: url.search,
        state: {
          ...location?.state,
          scrollToItemIndex: undefined,
          keywordSubmitted: options?.keywordSubmitted,
          searchContext: options?.searchContext,
        },
      });
    },
    [params, buildUrl, fetchResults, history, location, dispatch, searchContext],
  );

  /**
   * Skip the next URL-driven effect (used during state cache restoration).
   */
  const skipNextEffect = useCallback(() => {
    skipNextEffectRef.current = true;
  }, []);

  /**
   * Paginate convenience methods.
   */
  const paginate = useCallback(
    ({ onCachedResults }: { onCachedResults?: () => void } = {}) => {
      const currentPage = Math.max(1, Number(params.page) || 1);
      return {
        nextPage: () => {
          const nextPage = currentPage + 1;
          if (getPageCache) {
            const cached = getPageCache(nextPage);
            if (cached && cached.length > 0) {
              onCachedResults?.();
            }
          }
          performSearch({ page: nextPage });
        },
        prevPage: () => {
          const prevPage = currentPage > 2 ? currentPage - 1 : 1;
          if (getPageCache) {
            const cached = getPageCache(prevPage);
            if (cached && cached.length > 0) {
              onCachedResults?.();
            }
          }
          performSearch({ page: prevPage });
        },
      };
    },
    [params.page, getPageCache, performSearch],
  );

  return {
    params,
    performSearch,
    paginate,
    skipNextEffect,
    dispatch,
  };
}
